// The controls over a call. The bottom bar holds mic, camera, and hang up;
// everything else lives in a vertical "..." at the top right (Adam,
// 2026-09-28: "i want everything other than camera mic and hang up to live in
// a vertical ..."). There is no Share link button there any more (Adam,
// 2026-10-10: "don't have the share link button at all in the top right"),
// and the "..." never lights up for what is on inside it (same day: "don't
// have the ... light up blue when something in there is active"). On a desktop-sized viewport the bar also carries Share
// screen and Record between camera and hang up, and the menu drops them
// (2026-10-02, see useDesktopControls). Icons only on the round buttons; the
// menu has words.
// Every control keeps an accessible name that tracks its state, which is
// also what screen readers and the e2e suite find it by.

import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { useDesktopControls } from "../hooks/useDesktopControls";
import {
  BreezeMarkIcon,
  CameraIcon,
  ChatIcon,
  BlurIcon,
  PrepIcon,
  CameraOffIcon,
  CheckIcon,
  FlipCameraIcon,
  HandIcon,
  HangUpIcon,
  MicIcon,
  MicOffIcon,
  MoreVerticalIcon,
  PhoneIcon,
  PopoutIcon,
  RecordIcon,
  ScreenShareIcon,
} from "./icons";

// FaceTime's look (Adam, 2026-10-18: "Let's make our buttons this style
// instead of line outlines", QUEUE row 428): a control that is on is a white
// button with a dark glyph, one that is off (or a plain action) is dark glass
// with a white glyph, and hang up is red. Recording is white with a red dot.
// Hang up is the app's own red in both themes: dark mode's --danger is a soft
// salmon for text, and on a button it read as faded ("why does hang up look so
// faded", QUEUE row 435).
type Tone = "glass" | "lit" | "danger" | "live";

const HANG_UP_RED = "#ff453a";

const BACKGROUND: Readonly<Record<Tone, string>> = {
  glass: "rgba(58,58,60,0.62)",
  lit: "#fff",
  danger: HANG_UP_RED,
  live: "#fff",
};

const COLOR: Readonly<Record<Tone, string>> = {
  glass: "#fff",
  lit: "#111418",
  danger: "#fff",
  live: "var(--danger)",
};

interface ControlButtonProps {
  readonly label: string;
  readonly tone: Tone;
  readonly pressed?: boolean;
  readonly expanded?: boolean;
  /** What `expanded` opens: a menu (the default) or a docked panel. */
  readonly popup?: "menu" | "panel";
  readonly onClick: () => void;
  readonly children: ReactNode;
}

function ControlButton({ label, tone, pressed, expanded, popup = "menu", onClick, children }: ControlButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-haspopup={expanded === undefined || popup === "panel" ? undefined : "menu"}
      style={{
        display: "grid",
        placeItems: "center",
        width: 48,
        height: 48,
        flex: "0 0 auto",
        borderRadius: "50%",
        border: "none",
        padding: 0,
        background: BACKGROUND[tone],
        color: COLOR[tone],
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

interface MenuItemProps {
  readonly label: string;
  readonly icon: ReactNode;
  readonly checked?: boolean;
  readonly live?: boolean;
  /** A count shown at the right, for what is waiting. */
  readonly count?: number;
  readonly onClick: () => void;
}

function MenuItem({ label, icon, checked, live, count, onClick }: MenuItemProps): JSX.Element {
  return (
    <button
      type="button"
      role={checked === undefined ? "menuitem" : "menuitemcheckbox"}
      aria-checked={checked}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        minHeight: 46,
        padding: "0 12px",
        border: "none",
        borderRadius: 10,
        background: "transparent",
        color: live ? "#ff8a80" : "#fff",
        font: "inherit",
        fontSize: 15,
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span style={{ display: "grid", placeItems: "center", width: 22, flex: "none" }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {count ? <span style={{ color: "var(--accent)", fontWeight: 650, fontSize: 14 }}>{count > 9 ? "9+" : count}</span> : null}
      {checked !== undefined ? (
        <span style={{ color: "var(--accent)", visibility: checked ? "visible" : "hidden" }}>
          <CheckIcon size={18} />
        </span>
      ) : null}
    </button>
  );
}

interface CallMenuProps {
  readonly canFlipCamera: boolean;
  readonly onFlipCamera?: () => void;
  readonly canShareScreen: boolean;
  readonly sharingScreen: boolean;
  readonly onToggleScreenShare?: () => void;
  /** Host only; absent for everyone else. */
  readonly recording?: boolean;
  readonly onToggleRecording?: () => void;
  readonly originalSoundOn: boolean;
  readonly onToggleOriginalSound?: () => void;
  /** Blur the camera's background; absent where the browser cannot. */
  readonly blurOn?: boolean;
  readonly onToggleBlur?: () => void;
  /** Raise or lower this person's hand; absent before they are in the call. */
  readonly handRaised?: boolean;
  readonly onToggleHand?: () => void;
  /** The room's owner, signed in: dial a phone number into the call. */
  readonly onCallPhone?: () => void;
  /** Open or close the Breeze panel. Shown here below desktop width; on
   * desktop the bottom bar carries the button. */
  readonly onToggleBreeze?: () => void;
  readonly breezeOpen?: boolean;
  /** Open the in-call chat. Shown here below desktop width; on desktop the
   * bottom bar carries the button. */
  readonly onToggleChat?: () => void;
  readonly chatUnread?: number;
  /** Open this call's prep page. Offered only when the host has one. */
  readonly onOpenPrep?: () => void;
  /** With this, the Prep item shows whether the prep is open (desktop dock). */
  readonly prepOpen?: boolean;
}

/** The top right: the vertical "..." that opens the rest. The menu closes on a choice, a tap anywhere else, or Escape. */
export function CallMenu({
  canFlipCamera,
  onFlipCamera,
  canShareScreen,
  sharingScreen,
  onToggleScreenShare,
  recording,
  onToggleRecording,
  originalSoundOn,
  onToggleOriginalSound,
  blurOn,
  onToggleBlur,
  handRaised,
  onToggleHand,
  onCallPhone,
  onToggleBreeze,
  breezeOpen,
  onToggleChat,
  chatUnread,
  onOpenPrep,
  prepOpen,
}: CallMenuProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  // On desktop the bottom bar owns Share screen and Record.
  const desktop = useDesktopControls();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (action?: () => void) => () => {
    setOpen(false);
    action?.();
  };

  const items: JSX.Element[] = [];
  if (canFlipCamera && onFlipCamera) {
    items.push(<MenuItem key="flip" label="Flip camera" icon={<FlipCameraIcon size={20} />} onClick={choose(onFlipCamera)} />);
  }
  if (!desktop && canShareScreen && onToggleScreenShare) {
    items.push(
      <MenuItem
        key="screen"
        label={sharingScreen ? "Stop sharing screen" : "Share screen"}
        icon={<ScreenShareIcon size={20} />}
        onClick={choose(onToggleScreenShare)}
      />,
    );
  }
  if (!desktop && onToggleRecording) {
    items.push(
      <MenuItem
        key="record"
        label={recording ? "Stop recording" : "Record"}
        live={recording}
        icon={<RecordIcon size={18} />}
        onClick={choose(onToggleRecording)}
      />,
    );
  }
  if (!desktop && onToggleBreeze) {
    items.push(
      <MenuItem
        key="breeze"
        label="Breeze"
        icon={<BreezeMarkIcon size={20} />}
        onClick={choose(onToggleBreeze)}
      />,
    );
  }
  if (!desktop && onToggleChat) {
    items.push(<MenuItem key="chat" label="Chat" count={chatUnread} icon={<ChatIcon size={20} />} onClick={choose(onToggleChat)} />);
  }
  if (onOpenPrep) {
    items.push(<MenuItem key="prep" label="Agenda" checked={prepOpen} icon={<PrepIcon size={20} />} onClick={choose(onOpenPrep)} />);
  }
  if (onCallPhone) {
    items.push(<MenuItem key="phone" label="Call a phone" icon={<PhoneIcon size={20} />} onClick={choose(onCallPhone)} />);
  }
  if (onToggleHand) {
    items.push(
      <MenuItem
        key="hand"
        label={handRaised ? "Lower hand" : "Raise hand"}
        checked={handRaised === true}
        icon={<HandIcon size={20} />}
        onClick={choose(onToggleHand)}
      />,
    );
  }
  if (onToggleBlur) {
    items.push(
      <MenuItem key="blur" label="Blur background" checked={blurOn} icon={<BlurIcon size={20} />} onClick={choose(onToggleBlur)} />,
    );
  }
  if (onToggleOriginalSound) {
    items.push(
      <MenuItem
        key="sound"
        label="Original sound"
        checked={originalSoundOn}
        icon={<MicIcon size={20} />}
        onClick={choose(onToggleOriginalSound)}
      />,
    );
  }
  if (items.length === 0) return null;

  return (
    <div
      ref={rootRef}
      style={{
        position: "absolute",
        top: "calc(env(safe-area-inset-top, 0px) + var(--space-3))",
        right: "calc(env(safe-area-inset-right, 0px) + var(--space-3))",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "var(--space-2)",
        // Above the Breeze strip while its menu is open, so the items are not under it.
        zIndex: open ? 46 : 28,
      }}
    >
      {items.length > 0 ? (
        <ControlButton label="More" tone="glass" expanded={open} onClick={() => setOpen((v) => !v)}>
          <MoreVerticalIcon />
        </ControlButton>
      ) : null}
      {open ? (
        <div
          role="menu"
          style={{
            minWidth: 220,
            padding: 6,
            borderRadius: 14,
            background: "rgba(28,31,36,0.92)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
            animation: "breeze-fade 0.14s var(--ease)",
          }}
        >
          {items}
        </div>
      ) : null}
    </div>
  );
}

interface ControlsTrayProps {
  readonly micOn: boolean;
  readonly cameraOn: boolean;
  readonly onToggleMic: () => void;
  readonly onToggleCamera: () => void;
  readonly onLeave: () => void;
  /** Share screen and Record show in the bar on desktop only; absent in the lobby. */
  readonly canShareScreen?: boolean;
  readonly sharingScreen?: boolean;
  readonly onToggleScreenShare?: () => void;
  /** Host only; absent for everyone else. */
  readonly recording?: boolean;
  readonly onToggleRecording?: () => void;
  /** Desktop only: the Breeze button between Record and Leave. */
  readonly onToggleBreeze?: () => void;
  readonly breezeOpen?: boolean;
  /** Desktop only: the Chat button between Breeze and Leave. */
  readonly onToggleChat?: () => void;
  readonly chatOpen?: boolean;
  readonly chatUnread?: number;
  /** Desktop, in a browser that has Document Picture-in-Picture: the pop-out
   * button after Share screen. Absent where the browser cannot do it. */
  readonly onTogglePopout?: () => void;
  readonly popoutOpen?: boolean;
}

/** The bottom bar: mic, camera, and hang up; on desktop also Share screen,
 * Pop out (where the browser has it), Record, and Breeze, in that order before
 * hang up. */
export function ControlsTray({
  micOn,
  cameraOn,
  onToggleMic,
  onToggleCamera,
  onLeave,
  canShareScreen,
  sharingScreen,
  onToggleScreenShare,
  recording,
  onToggleRecording,
  onToggleBreeze,
  breezeOpen,
  onToggleChat,
  chatOpen,
  chatUnread,
  onTogglePopout,
  popoutOpen,
}: ControlsTrayProps): JSX.Element {
  const desktop = useDesktopControls();
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + var(--space-4))",
        paddingTop: 48,
        paddingLeft: "var(--space-4)",
        paddingRight: "var(--space-4)",
        background: "linear-gradient(to top, rgba(0,0,0,0.45), transparent)",
        display: "flex",
        justifyContent: "center",
        gap: "var(--space-4)",
        zIndex: 10,
      }}
    >
      <ControlButton label={micOn ? "Mic" : "Muted"} tone={micOn ? "lit" : "glass"} pressed={micOn} onClick={onToggleMic}>
        {micOn ? <MicIcon /> : <MicOffIcon />}
      </ControlButton>
      <ControlButton
        label={cameraOn ? "Camera" : "Camera off"}
        tone={cameraOn ? "lit" : "glass"}
        pressed={cameraOn}
        onClick={onToggleCamera}
      >
        {cameraOn ? <CameraIcon /> : <CameraOffIcon />}
      </ControlButton>
      {desktop && canShareScreen && onToggleScreenShare ? (
        <ControlButton
          label={sharingScreen ? "Stop sharing screen" : "Share screen"}
          tone={sharingScreen ? "lit" : "glass"}
          onClick={onToggleScreenShare}
        >
          <ScreenShareIcon />
        </ControlButton>
      ) : null}
      {desktop && onTogglePopout ? (
        <ControlButton label={popoutOpen ? "Close pop out" : "Pop out"} tone={popoutOpen ? "lit" : "glass"} onClick={onTogglePopout}>
          <PopoutIcon size={22} />
        </ControlButton>
      ) : null}
      {desktop && onToggleRecording ? (
        <ControlButton label={recording ? "Stop recording" : "Record"} tone={recording ? "live" : "glass"} onClick={onToggleRecording}>
          <RecordIcon size={20} />
        </ControlButton>
      ) : null}
      {desktop && onToggleBreeze ? (
        <ControlButton
          label="Breeze"
          tone={breezeOpen ? "lit" : "glass"}
          expanded={breezeOpen === true}
          popup="panel"
          onClick={onToggleBreeze}
        >
          <BreezeMarkIcon size={22} />
        </ControlButton>
      ) : null}
      {desktop && onToggleChat ? (
        <span style={{ position: "relative", display: "inline-flex" }}>
          <ControlButton
            label={chatUnread ? `Chat, ${chatUnread} new` : "Chat"}
            tone={chatOpen ? "lit" : "glass"}
            expanded={chatOpen === true}
            popup="panel"
            onClick={onToggleChat}
          >
            <ChatIcon size={22} />
          </ControlButton>
          {chatUnread && !chatOpen ? (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -2,
                right: -2,
                minWidth: 18,
                height: 18,
                padding: "0 5px",
                borderRadius: 9,
                background: "var(--accent)",
                color: "var(--accent-contrast)",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: "18px",
                textAlign: "center",
              }}
            >
              {chatUnread > 9 ? "9+" : chatUnread}
            </span>
          ) : null}
        </span>
      ) : null}
      <ControlButton label="Leave" tone="danger" onClick={onLeave}>
        <HangUpIcon />
      </ControlButton>
    </div>
  );
}
